import { useQuery } from '@tanstack/react-query';
import { fetchProjectSettings } from '../api/projects';
import { resolvePlan, type ProjectPlan } from '../../../../shared/types/plans';

/**
 * Canonical way to read a PROJECT's effective plan in the portal.
 *
 * Plan/feature gating in project-scoped editors (MCP servers, Workflow,
 * multi-model, …) MUST key off the *project's* plan — authoritative on the
 * project's BILLING/META row and surfaced into the settings view by
 * `GET /projects/:id/settings`. It must NOT use the logged-in account's plan
 * (`currentAccount.plan`): a Business project has to stay unlocked even when the
 * operator's personal account default is lower. We shipped exactly that bug in
 * McpServersTab/WorkflowTab once — this hook (plus the guard test in
 * `__tests__/projects/no-account-plan-gating.test.ts`) makes it the one path.
 *
 * Shares the `['project', id, 'settings']` query key, so it dedupes with a tab's
 * own settings fetch — no extra network round-trip.
 */
export function useProjectPlan(projectId: string): {
  plan: ProjectPlan;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
    enabled: projectId.length > 0,
  });
  return {
    plan: resolvePlan({ plan: q.data?.settings.plan, tier: q.data?.settings.tier }),
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
